/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { styled, SvgIcon, SvgIconProps } from '@mui/material'
import {
  aglynLogoClassKeys,
  type AglynLogoProps,
} from './aglyn-logo-class-keys'

/**
 * The Aglyn mark and logotype side by side.
 *
 * The one wordmark a published customer site can render: the navigation
 * loader falls back to it when a host has set no logo of its own. It sits
 * apart from the console and besigner wordmarks in `svg-icons.tsx` so that
 * fallback does not carry the two authoring marks — which no customer page
 * can draw — onto every page of every site.
 */
export const AglynLogoFull = styled(
  (props: SvgIconProps) => {
    return (
      <SvgIcon {...props} viewBox="0 0 79 24">
        <title>Aglyn</title>
        <g id="aglyn-logo-full-light">
          <g id="aglyn-logo-mark-multi">
            <path
              className={aglynLogoClassKeys.compass}
              d="M5,16.235l-0.267,0.184l-1.733,0.152l-0,1.459l-1,0l-0,4l4,0l0,-1l7.171,0l-0.148,-0.262l-0,-1.738l-7.023,0l0,-1l-1,0l0,-1.795Zm14,-7.233l0,9.028l-0.004,0l0,2.738l-0.714,1.262l3.718,0l0,-4l-1,0l0,-10.273c-0.37,0 -0.836,-0 -0.836,-0c-0.307,0.47 -0.697,0.893 -1.164,1.245Zm-16,0.145l2,-1.376l0,-1.741l1,0l0,-1l4.748,0l0.279,-0.192c0.025,-0.624 0.167,-1.238 0.416,-1.808l-5.443,0l0,-1l-4,0l-0,4l1,0l-0,3.117Zm19,-7.117l-2.029,0c0.067,0.088 0.131,0.179 0.192,0.273l1.281,0l0.556,0.556l0,-0.829Z"
            />
            <path
              className={aglynLogoClassKeys.boundingBox}
              d="M15,10.03c0.323,0.073 0.661,0.112 1.01,0.112c0.348,0 0.687,-0.039 1.009,-0.112l0,10.217l-1.009,1.783l-1.01,-1.783l0,-10.217Zm-1.898,-4.22c-0.345,-1.281 0.196,-2.684 1.398,-3.378c1.434,-0.828 3.27,-0.336 4.098,1.098c0.139,0.241 0.241,0.493 0.307,0.75l1.72,0l0.375,0.375l-0,0.75l-0.375,0.375l-1.721,0c-0.194,0.752 -0.679,1.429 -1.404,1.848c-1.052,0.607 -2.319,0.505 -3.247,-0.159l-3.458,2.379l-0.252,1.364l-3.296,2.267l-1.363,-0.252l-1.843,1.268l-2.041,0.179l0.897,-1.843l1.826,-1.256c0,-0 0.257,-1.391 0.257,-1.391l3.296,-2.267l1.39,0.257l3.436,-2.364Zm3.398,0.086c-0.478,0.276 -1.09,0.112 -1.366,-0.366c-0.276,-0.478 -0.112,-1.09 0.366,-1.366c0.478,-0.276 1.09,-0.112 1.366,0.366c0.276,0.478 0.112,1.09 -0.366,1.366Z"
            />
          </g>
          <g id="aglyn-logo-text-light">
            <path
              className={aglynLogoClassKeys.textAglyn}
              d="M55.612,19.363c0.246,0.058 0.482,0.107 0.707,0.146c0.224,0.04 0.414,0.06 0.57,0.06c0.257,-0 0.477,-0.066 0.658,-0.196c0.182,-0.131 0.355,-0.366 0.518,-0.707c0.163,-0.34 0.34,-0.815 0.53,-1.424l-4.385,-10.907l2.938,-0l2.995,8.059l2.618,-8.059l2.697,-0l-4.645,13.065c-0.165,0.495 -0.423,0.941 -0.774,1.338c-0.352,0.397 -0.781,0.709 -1.289,0.935c-0.507,0.226 -1.08,0.339 -1.717,0.339c-0.228,0 -0.457,-0.018 -0.686,-0.053c-0.229,-0.036 -0.475,-0.097 -0.735,-0.182l-0,-2.414Zm-13.445,-2.052c-0.734,0 -1.408,-0.145 -2.023,-0.434c-0.614,-0.289 -1.14,-0.689 -1.579,-1.2c-0.438,-0.511 -0.777,-1.1 -1.017,-1.766c-0.241,-0.667 -0.361,-1.373 -0.361,-2.12c0,-0.789 0.125,-1.525 0.375,-2.208c0.25,-0.684 0.604,-1.284 1.062,-1.802c0.458,-0.517 1,-0.92 1.626,-1.209c0.627,-0.289 1.32,-0.434 2.081,-0.434c0.859,0 1.614,0.192 2.266,0.577c0.651,0.385 1.184,0.899 1.599,1.541l-0,-1.921l2.477,-0l0,10.388c0,1.078 -0.264,2.002 -0.793,2.772c-0.529,0.771 -1.256,1.362 -2.182,1.774c-0.925,0.411 -1.979,0.617 -3.162,0.617c-1.208,0 -2.227,-0.199 -3.058,-0.598c-0.83,-0.399 -1.541,-0.954 -2.132,-1.665l1.545,-1.491c0.424,0.519 0.956,0.925 1.596,1.216c0.641,0.291 1.324,0.437 2.049,0.437c0.594,-0 1.141,-0.108 1.642,-0.323c0.501,-0.214 0.903,-0.548 1.207,-1.001c0.304,-0.453 0.456,-1.032 0.456,-1.738l0,-1.371c-0.364,0.623 -0.88,1.105 -1.547,1.446c-0.667,0.342 -1.376,0.513 -2.127,0.513Zm-16.167,-3.288c-0,-0.713 0.202,-1.335 0.607,-1.867c0.405,-0.532 0.964,-0.946 1.678,-1.242c0.714,-0.297 1.536,-0.445 2.466,-0.445c0.468,-0 0.941,0.036 1.417,0.11c0.476,0.073 0.894,0.183 1.253,0.329l0,-0.584c0,-0.689 -0.207,-1.222 -0.622,-1.597c-0.416,-0.376 -1.031,-0.564 -1.846,-0.564c-0.613,0 -1.194,0.105 -1.744,0.314c-0.551,0.208 -1.129,0.511 -1.737,0.907l-0.895,-1.817c0.731,-0.479 1.48,-0.837 2.246,-1.074c0.767,-0.237 1.575,-0.355 2.427,-0.355c1.568,0 2.794,0.388 3.678,1.165c0.884,0.777 1.326,1.89 1.326,3.34l-0,3.484c-0,0.291 0.05,0.498 0.151,0.621c0.102,0.123 0.274,0.194 0.517,0.215l-0,2.279c-0.247,0.047 -0.472,0.082 -0.676,0.105c-0.204,0.023 -0.38,0.035 -0.528,0.035c-0.564,-0 -0.99,-0.123 -1.278,-0.368c-0.289,-0.245 -0.466,-0.553 -0.532,-0.923l-0.061,-0.556c-0.486,0.617 -1.087,1.091 -1.805,1.421c-0.718,0.33 -1.451,0.495 -2.199,0.495c-0.733,-0 -1.392,-0.151 -1.976,-0.453c-0.584,-0.301 -1.041,-0.712 -1.371,-1.231c-0.331,-0.519 -0.496,-1.101 -0.496,-1.744Zm24.558,-11.982l2.833,0l-0,11.695c-0,0.51 0.086,0.851 0.258,1.024c0.172,0.173 0.4,0.26 0.684,0.26c0.239,0 0.476,-0.03 0.712,-0.089c0.236,-0.059 0.443,-0.13 0.62,-0.212l0.391,2.138c-0.386,0.169 -0.831,0.304 -1.333,0.406c-0.502,0.102 -0.963,0.153 -1.382,0.153c-0.878,0 -1.561,-0.234 -2.05,-0.702c-0.488,-0.469 -0.733,-1.129 -0.733,-1.983l0,-12.69Zm26.442,15.201l-2.832,0l-0,-6.118c-0,-0.877 -0.155,-1.516 -0.463,-1.919c-0.308,-0.403 -0.737,-0.605 -1.285,-0.605c-0.381,0 -0.765,0.099 -1.154,0.295c-0.389,0.197 -0.738,0.465 -1.046,0.804c-0.308,0.338 -0.537,0.729 -0.687,1.172l-0,6.371l-2.833,0l0,-10.907l2.559,-0l-0,2.013c0.283,-0.464 0.645,-0.86 1.084,-1.187c0.44,-0.328 0.943,-0.581 1.509,-0.758c0.566,-0.176 1.171,-0.265 1.812,-0.265c0.692,0 1.257,0.122 1.696,0.367c0.44,0.245 0.778,0.574 1.015,0.986c0.237,0.413 0.4,0.873 0.49,1.382c0.09,0.508 0.135,1.026 0.135,1.553l-0,6.816Zm-44.119,-2.674c0.163,-0.157 0.294,-0.32 0.392,-0.49c0.099,-0.17 0.148,-0.325 0.148,-0.463l0,-1.11c-0.341,-0.129 -0.709,-0.231 -1.102,-0.305c-0.393,-0.075 -0.768,-0.112 -1.125,-0.112c-0.735,-0 -1.337,0.154 -1.808,0.463c-0.47,0.308 -0.705,0.719 -0.705,1.232c-0,0.278 0.077,0.543 0.233,0.794c0.155,0.251 0.38,0.454 0.674,0.609c0.294,0.155 0.649,0.233 1.065,0.233c0.425,0 0.84,-0.08 1.243,-0.24c0.404,-0.16 0.733,-0.364 0.985,-0.611Zm10.247,0.511c0.306,0 0.599,-0.049 0.88,-0.147c0.28,-0.098 0.541,-0.23 0.781,-0.397c0.24,-0.166 0.451,-0.362 0.634,-0.588c0.183,-0.226 0.322,-0.464 0.418,-0.713l0,-2.597c-0.165,-0.425 -0.4,-0.797 -0.703,-1.116c-0.302,-0.319 -0.642,-0.567 -1.018,-0.745c-0.376,-0.178 -0.763,-0.267 -1.161,-0.267c-0.436,0 -0.832,0.093 -1.187,0.279c-0.355,0.186 -0.659,0.439 -0.911,0.76c-0.253,0.321 -0.448,0.68 -0.586,1.078c-0.137,0.398 -0.206,0.81 -0.206,1.234c0,0.448 0.078,0.866 0.234,1.254c0.156,0.388 0.372,0.73 0.646,1.027c0.275,0.297 0.6,0.527 0.976,0.692c0.376,0.164 0.777,0.246 1.203,0.246Z"
            />
          </g>
        </g>
      </SvgIcon>
    )
  },
  {
    name: 'AglynLogoFull',
    shouldForwardProp: (prop) => prop !== 'variant',
  },
)<AglynLogoProps>(({ theme, variant }) => {
  let compass: string
  let boundingBox: string
  let textAglyn: string

  switch (variant) {
    case 'white':
      compass = theme.palette.common.white
      boundingBox = theme.palette.common.white
      textAglyn = theme.palette.common.white
      break
    case 'black':
      compass = theme.palette.common.black
      boundingBox = theme.palette.common.black
      textAglyn = theme.palette.common.white
      break
    case 'light':
      compass = theme.palette.primary.main
      boundingBox = theme.palette.secondary.main
      textAglyn = theme.palette.common.white
      break
    case 'dark':
      boundingBox = theme.palette.primary.main
      compass = theme.palette.secondary.main
      textAglyn = theme.palette.common.black
      break
    default:
      boundingBox = theme.palette.primary.main
      compass = theme.palette.secondary.main
      textAglyn =
        theme.palette.mode === 'light'
          ? theme.palette.primary.main
          : theme.palette.primary.main
      break
  }

  return {
    fillRule: 'evenodd',
    height: 'auto',

    [`& .${aglynLogoClassKeys.boundingBox}`]: {
      fill: boundingBox,
    },
    [`& .${aglynLogoClassKeys.compass}`]: {
      fill: compass,
    },
    [`& .${aglynLogoClassKeys.textAglyn}`]: {
      fill: textAglyn,
    },
  }
})
